// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportConfiguration.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, observable } from 'mobx';
import moment from 'moment';
import tracker from 'share/tracking/Izenda.Tracker';
import constants from 'logic/data/Izenda.SystemConfigurationConstants';
import { DATE_FORMAT } from 'logic/data/Izenda.AppGlobalConstants';
import { isEqual, trim } from 'lodash/fp';
import UiState from 'logic/Izenda.UiState';

@tracker
export default class ReportConfiguration {
  @observable enforceVersionHistory = false;
  @observable numOfArchivedVersionToKeep = 0;
  @observable removeArchivedVersions = false;
  @observable
  recurrentReportSetting = {
    once: false,
    recurrence: false,
    startDate: null,
    startTime: null,
    recurrenceType: 0,
    occurValue: 0,
    atDate: null,
    atTime: null
  };
  @observable
  defaultImageUrl = {
    value: ''
  };
  @observable globalCategoryName = constants.DEFAULT_VALUES.GLOBAL_NAME;
  @observable localCategoryName = constants.DEFAULT_VALUES.LOCAL_NAME;
  @observable
  defaultColorTheme = {
    value: undefined
  };

  /**
   * Init instant
   * @param {Object} data
   */
  constructor(data: any) {
    this.numOfArchivedVersionToKeepDefault = 0;
    data && this.populateData(data);
  }

  /**
   * Reset data
   */
  @action
  resetData() {
    this.resetDirty();
    this.defaultColorTheme.value = undefined;
  }

  /**
   * populate data
   * @param {Object} data
   */
  @action
  populateData(data: any): void {
    this.enforceVersionHistory = data.enforceVersionHistory;
    this.numOfArchivedVersionToKeep = data.numOfArchivedVersionToKeep;
    this.numOfArchivedVersionToKeepDefault = data.numOfArchivedVersionToKeepDefault;
    this.removeArchivedVersions = data.removeArchivedVersions;
    this.globalCategoryName = data.globalCategoryName || constants.DEFAULT_VALUES.GLOBAL_NAME;
    this.localCategoryName = data.localCategoryName || constants.DEFAULT_VALUES.LOCAL_NAME;

    this.populateRecurrentReport(data.recurrentReportSetting);
    this.resetDirty();
  }

  /**
   * Populate recurrent report
   * @param {Object} data
   */
  @action
  populateRecurrentReport(data: any): void {
    this.recurrentReportSetting.once = data.once;
    this.recurrentReportSetting.recurrence = data.recurrence;
    this.recurrentReportSetting.recurrenceType = data.recurrenceType;
    this.recurrentReportSetting.occurValue = data.occurValue;
    this.setupDate(data.once, data);
  }

  /**
   * Setup date
   * @param {Object} once
   * @param {Object} data
   */
  @action
  setupDate(once: any, data: any): void {
    data = data || {};
    const startDate = data.startDate ? moment(data.startDate).format(UiState.currentUser.dateFormat) : null;

    if (once) {
      this.recurrentReportSetting.atDate = startDate;
      this.recurrentReportSetting.atTime = data.startTime || null;
      this.recurrentReportSetting.startDate = null;
      this.recurrentReportSetting.startTime = null;
    } else {
      this.recurrentReportSetting.startDate = startDate;
      this.recurrentReportSetting.startTime = data.startTime || null;
      this.recurrentReportSetting.atDate = null;
      this.recurrentReportSetting.atTime = null;
    }
  }

  /**
   * Compose saving data
   * @returns {Object}
   */
  buildDataForSaving(): any {
    const recurrence = this.recurrentReportSetting;
    const date = recurrence.once ? recurrence.atDate : recurrence.startDate;
    const time = recurrence.once ? recurrence.atTime : recurrence.startTime;
    const dateFormat = UiState.currentUser.dateFormat;
    const dateFormatted = moment(date, dateFormat).isValid()
      ? moment(date, dateFormat).format(DATE_FORMAT)
      : date;

    return {
      enforceVersionHistory: this.enforceVersionHistory,
      numOfArchivedVersionToKeep: this.numOfArchivedVersionToKeep,
      removeArchivedVersions: this.removeArchivedVersions,
      recurrentReportSetting: {
        once: recurrence.once,
        recurrence: recurrence.recurrence,
        startDate: dateFormatted,
        startTime: time,
        recurrenceType: recurrence.recurrenceType,
        occurValue: recurrence.occurValue
      },
      globalCategoryName: this.globalCategoryName,
      localCategoryName: this.localCategoryName
    };
  }

  /**
   * Update property by key
   * @param {string} key
   * @param {Object} value
   */
  @action
  setValueByKey(key: string, value: any): void {
    switch (key) {
      case 'enforceVersionHistory':
        this.enforceVersionHistory = value;
        break;
      case 'numOfArchivedVersionToKeep':
        this.numOfArchivedVersionToKeep = parseInt(value) || 0;
        break;
      case 'removeArchivedVersions':
        this.removeArchivedVersions = value;
        break;
      case 'occurValue':
        this.recurrentReportSetting.occurValue = parseInt(value) || 0;
        break;
      case 'startDate':
        this.recurrentReportSetting.startDate = value;
        break;
      case 'startTime':
        this.recurrentReportSetting.startTime = value;
        break;
      case 'atDate':
        this.recurrentReportSetting.atDate = value;
        break;
      case 'atTime':
        this.recurrentReportSetting.atTime = value;
        break;
      case 'defaultImageUrl':
        this.defaultImageUrl.value = value;
        break;
      case 'globalCategoryName':
        this.globalCategoryName = value;
        break;
      case 'localCategoryName':
        this.localCategoryName = value;
        break;
      case 'defaultColorTheme':
        this.defaultColorTheme.value = value;
        break;
    }
    this.markDirty();
  }

  /**
   * Update type archived
   * @param {string} key
   * @param {Object} value
   */
  @action
  setTypeArchived(key: string, value: any): void {
    switch (key) {
      case 'once':
        this.recurrentReportSetting.once = true;
        this.recurrentReportSetting.recurrence = false;
        break;
      case 'recurrence':
        this.recurrentReportSetting.once = false;
        this.recurrentReportSetting.recurrence = true;
        break;
    }
    this.setupDate(key === 'once');
    this.markDirty();
  }

  /**
   * reset num of archived version to keep.
   */
  resetNumOfArchivedVersionToKeep(): void {
    this.numOfArchivedVersionToKeep = this.numOfArchivedVersionToKeepDefault;
  }

  /**
   * Update num of archived version to keep.
   * @param {Object} value
   */
  setNumOfArchivedVersionToKeep(value: any): void {
    var newValue = parseInt(value) || 0;
    if (newValue >= 0 && newValue <= 20) {
      this.numOfArchivedVersionToKeep = newValue;
    }
  }

  /**
   * Update recurrence key
   * @param {Object} key
   * @param {Object} value
   */
  @action
  setRecurrenceType(key: any, value: any): void {
    this.recurrentReportSetting.recurrenceType = key;
    this.markDirty();
  }

  /**
   * Check duplicate global and local names.
   * @returns {boolean}
   */
  doesDuplicateGlobalAndLocalName(): boolean {
    return !!this.globalCategoryName && isEqual(trim(this.globalCategoryName), trim(this.localCategoryName));
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportConfiguration.js